/**
 * BookLockBar (iter-46, iter-47 alert-dialog) — surfaces who currently
 * holds the edit lock on a workspace-shared book. Personal books hide
 * this bar entirely. Force-release now uses shadcn AlertDialog instead
 * of window.confirm to match the rest of BookForge.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, LockOpen } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function _relative(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.max(0, Math.round((now - then) / 1000));
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return `${Math.round(diff / 86400)}d ago`;
  } catch { return ""; }
}

export function BookLockBar({ book, onBookUpdated }) {
  const { user } = useAuth();
  const [lock, setLock] = useState(book?.lock || null);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  const refresh = useCallback(async () => {
    if (!book?.id) return;
    try {
      const { data } = await api.get(`/books/${book.id}/lock`);
      if (data?.held) {
        setLock({
          held_by_id: data.user_id, nickname: data.nickname,
          taken_at: data.taken_at, is_me: !!data.is_me,
        });
      } else {
        setLock(null);
      }
    } catch (err) { /* silent — not all books are shared */ }
  }, [book?.id]);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => {
    if (!book?.workspace_id) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") refresh();
    }, 20000);
    return () => clearInterval(t);
  }, [book?.workspace_id, refresh]);

  if (!book?.workspace_id) return null; // personal books skip lock UI

  const take = async () => {
    setBusy(true);
    try {
      await api.post(`/books/${book.id}/lock`);
      toast.success("Lock acquired — you can edit safely");
      await refresh();
      onBookUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const release = async () => {
    setBusy(true);
    try {
      await api.post(`/books/${book.id}/unlock`);
      toast.success("Lock released");
      await refresh();
      onBookUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };
  const forceTake = async () => {
    setBusy(true);
    try {
      await api.post(`/books/${book.id}/lock/force`);
      toast.success("Lock forced — you now hold this book");
      setConfirmOpen(false);
      await refresh();
      onBookUpdated?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const wsName = book.workspace_name || "Shared workspace";

  if (!lock) {
    return (
      <div className="mt-4 border border-[color:var(--border)] bg-white px-3 py-2 flex items-center justify-between"
           data-testid="book-lock-bar">
        <div className="flex items-center gap-2 text-xs text-[color:var(--text-muted)]">
          <LockOpen className="w-3.5 h-3.5" />
          <span>Unlocked in <span className="text-[color:var(--primary)]">{wsName}</span> — click "Take lock" to prevent concurrent edits.</span>
        </div>
        <button onClick={take} disabled={busy}
                className="btn-ghost text-xs inline-flex items-center gap-1"
                data-testid="book-lock-take-btn">
          <Lock className="w-3 h-3"/> Take lock
        </button>
      </div>
    );
  }

  if (lock.is_me) {
    return (
      <div className="mt-4 border border-[color:var(--primary)] bg-[color:var(--canvas)] px-3 py-2 flex items-center justify-between"
           data-testid="book-lock-bar">
        <div className="flex items-center gap-2 text-xs">
          <Lock className="w-3.5 h-3.5 text-[color:var(--primary)]"/>
          <span>You're editing this book — held since {_relative(lock.taken_at)}. Autosave keeps your progress safe.</span>
        </div>
        <button onClick={release} disabled={busy}
                className="btn-ghost text-xs inline-flex items-center gap-1"
                data-testid="book-lock-release-btn">
          <Unlock className="w-3 h-3"/> Release lock
        </button>
      </div>
    );
  }

  return (
    <div className="mt-4 border border-amber-500 bg-amber-50 px-3 py-2 flex items-center justify-between"
         data-testid="book-lock-bar">
      <div className="flex items-center gap-2 text-xs text-amber-900">
        <Lock className="w-3.5 h-3.5"/>
        <span><span className="font-semibold">{lock.nickname}</span> is currently editing this book (taken {_relative(lock.taken_at)}). Read-only until they release.</span>
      </div>
      {isAdmin && (
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogTrigger asChild>
            <button className="btn-ghost text-xs inline-flex items-center gap-1 text-amber-900 border-amber-700"
                    data-testid="book-lock-force-btn">
              <Unlock className="w-3 h-3"/> Force release &amp; take
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="book-lock-force-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Force release {lock.nickname}'s lock?</AlertDialogTitle>
              <AlertDialogDescription>
                {lock.nickname} is currently editing this book. Forcing the lock
                takes over immediately &mdash; their unsaved edits since the last
                autosave may be lost. Continue?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="book-lock-force-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={forceTake} disabled={busy}
                                  data-testid="book-lock-force-confirm">
                Yes, force release &amp; take
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </div>
  );
}
